import type {
  DomainPropertyIR,
  DomainPropertySummary,
  FieldMetamodelManifestSpec,
  MetamodelGraphqlMutation,
} from "@granular-software/metamodel-core";
import { defineMetamodelPackage } from "@granular-software/metamodel-core";

export type TemporalFieldFormat = "date" | "date-time";

function normalizeTemporalFormat(
  format: FieldMetamodelManifestSpec["format"],
): TemporalFieldFormat | null {
  return format === "date" || format === "date-time" ? format : null;
}

export function buildTemporalFieldMutations(
  fieldPath: string,
  format: FieldMetamodelManifestSpec["format"],
): MetamodelGraphqlMutation[] {
  const normalized = normalizeTemporalFormat(format);
  if (!normalized) return [];

  return [
    {
      label: `set temporal format on ${fieldPath}`,
      query: `mutation { at(path: ${JSON.stringify(fieldPath)}) { set_temporal_format(format: ${JSON.stringify(normalized)}) { format } } }`,
    },
  ];
}

export const temporalMetamodelPackage = defineMetamodelPackage({
  id: "temporal",
  docs: {
    fieldRows: [
      {
        key: "format",
        description:
          "Temporal representation for date fields. Use `date` for a civil calendar date and `date-time` when the instant and time matter.",
      },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type TemporalFormatMetamodel {
        model: Model!
        format: String!
      }

      extend type Model {
        temporal_format: TemporalFormatMetamodel
      }

      extend type ModelMutation {
        set_temporal_format(format: String!): TemporalFormatMetamodel
      }
      `,
    ],
    createResolvers({ run }) {
      return {
        TemporalFormatMetamodel: {
          model: (value) => value.model,
          format: (value) => value.format,
        },
        Model: {
          temporal_format: async (ant) => await run(ant.temporal_format()),
        },
        ModelMutation: {
          set_temporal_format: async (ant, { format }) => {
            if (format !== "date" && format !== "date-time") {
              throw new Error(`Unsupported temporal format: ${format}`);
            }
            return {
              model: await run(ant.set_temporal_format(format)),
              format,
            };
          },
        },
      };
    },
  },
  manifest: {
    buildFieldMutations(fieldPath, spec) {
      return buildTemporalFieldMutations(fieldPath, spec.format);
    },
  },
  summary: {
    selections: {
      propertyFields: ["temporal_format { format }"],
    },
    readPropertySummary(rawProperty) {
      const format = rawProperty.temporal_format?.format;
      return format === "date" || format === "date-time" ? { format } : {};
    },
  },
  domain: {
    applyToPropertyIR(
      propertyIR: DomainPropertyIR,
      _propertySummary: DomainPropertySummary,
    ) {
      return propertyIR;
    },
  },
});
